import { useState } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";

interface StringArrayEditorProps {
  value: string[];
  onChange: (value: string[]) => void;
  title: string;
  description?: string;
  placeholder?: string;
  emptyMessage?: string;
  addButtonText?: string;
}

export default function StringArrayEditor({
  value,
  onChange,
  title,
  description,
  placeholder = "Zadejte text...",
  emptyMessage = "Zatím žádné položky",
  addButtonText = "Přidat položku",
}: StringArrayEditorProps) {
  const [newItem, setNewItem] = useState("");

  const addItem = () => {
    if (newItem.trim()) {
      onChange([...value, newItem.trim()]);
      setNewItem("");
    }
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, newValue: string) => {
    const updated = [...value];
    updated[index] = newValue;
    onChange(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        {description && (
          <p className="text-sm text-gray-500">{description}</p>
        )}
      </div>

      {/* Add new item */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={handleKeyDown}
          className="input flex-1"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={addItem}
          disabled={!newItem.trim()}
          className="btn-secondary"
        >
          <Plus className="h-4 w-4" />
          {addButtonText}
        </button>
      </div>

      {/* Items list */}
      {value.length === 0 ? (
        <p className="text-sm text-gray-500 italic">{emptyMessage}</p>
      ) : (
        <div className="space-y-2">
          {value.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
            >
              <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={item}
                onChange={(e) => updateItem(index, e.target.value)}
                className="flex-1 bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="p-1 text-gray-400 hover:text-error-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
